import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from "@sentry/react";
import {
    reportChunkRecovery,
    shouldRecoverFromChunkFailure,
    triggerChunkReload,
} from '../utils/chunkErrorHandler';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    eventId: string | null;
    /** true pendant le reload auto post-déploiement (évite le flash d'écran d'erreur) */
    recoveringChunk: boolean;
}

interface GlobalErrorFallbackProps {
    eventId: string | null;
    error: Error | null;
    onReset: () => void;
}

function GlobalErrorFallback({ eventId, error, onReset }: GlobalErrorFallbackProps) {
    const { t } = useTranslation('common');

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            textAlign: 'center',
            backgroundColor: '#f9f9f9',
            color: '#333'
        }}>
            <h1 style={{ fontSize: '24px', marginBottom: '16px', color: '#A5765C' }}>
                {t('globalErrorBoundary.title')}
            </h1>
            <p style={{ marginBottom: '24px', maxWidth: '500px', lineHeight: '1.5' }}>
                {t('globalErrorBoundary.description')}
            </p>

            <button
                onClick={onReset}
                style={{
                    padding: '12px 24px',
                    backgroundColor: '#A5765C',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 6px rgba(165, 118, 92, 0.2)',
                    marginBottom: '30px'
                }}
            >
                {t('globalErrorBoundary.resetButton')}
            </button>

            <div style={{
                fontSize: '12px',
                color: '#666',
                background: '#e0e0e0',
                padding: '10px',
                borderRadius: '4px',
                maxWidth: '90%',
                overflowWrap: 'anywhere'
            }}>
                {eventId && (
                    <p style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                        {t('globalErrorBoundary.supportId', { eventId })}
                    </p>
                )}
                {error && (
                    <p style={{ fontFamily: 'monospace' }}>
                        {error.toString()}
                    </p>
                )}
            </div>
        </div>
    );
}

class GlobalErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        eventId: null,
        recoveringChunk: false,
    };

    public static getDerivedStateFromError(error: Error): Partial<State> {
        if (shouldRecoverFromChunkFailure(error)) {
            return { hasError: false, error, eventId: null, recoveringChunk: true };
        }
        return { hasError: true, error, eventId: null, recoveringChunk: false };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        if (shouldRecoverFromChunkFailure(error)) {
            reportChunkRecovery('error_boundary');
            const reloading = triggerChunkReload();
            if (reloading) return;
            // Reload déjà tenté → afficher l'écran d'erreur classique
            this.setState({ hasError: true, recoveringChunk: false });
        }

        console.error('Uncaught error:', error, errorInfo);

        const eventId = Sentry.captureException(error, {
            contexts: {
                react: {
                    componentStack: errorInfo.componentStack
                }
            },
            tags: shouldRecoverFromChunkFailure(error)
                ? { chunk_load_error: 'true' }
                : undefined,
        });

        this.setState({ eventId });
    }

    private handleReset = () => {
        localStorage.clear();
        sessionStorage.clear();

        document.cookie.split(";").forEach((c) => {
            document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });

        window.location.reload();
    };

    public render() {
        if (this.state.recoveringChunk && !this.state.hasError) {
            return (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    color: '#666',
                    backgroundColor: '#f9f9f9',
                }}>
                    Mise à jour en cours…
                </div>
            );
        }

        if (this.state.hasError) {
            return (
                <GlobalErrorFallback
                    eventId={this.state.eventId}
                    error={this.state.error}
                    onReset={this.handleReset}
                />
            );
        }

        return this.props.children;
    }
}

export default GlobalErrorBoundary;
